/**
 * 定时显隐模块（schedule）
 *
 * 作用：按 HTML 上的时间窗属性，控制带标记容器的显示与隐藏，
 *      供活动 banner 等"按档期上下架"的内容使用。
 *      时间配置全部写在 HTML 上，调整档期不需要改 JS、不需要重新打包。
 *
 * ── 用法（写在模板里） ─────────────────────────────────────────────
 * <div class="part gwp-container schedule-managed"
 *      data-schedule-from="2026-08-01T09:00:00+08:00"
 *      data-schedule-to="2026-09-17T15:00:00+08:00"
 *      data-schedule-id="cpb__gwp_banner_2607"
 *      hidden>
 *   ...
 * </div>
 *
 * ── 契约 ───────────────────────────────────────────────────────────
 * 1. 容器标记：class="schedule-managed"
 *    - 唯一标记，配套样式见 Public/static/css/core/_states.scss
 *    - 纯标记类，只表示"受排期管理"这一身份；禁止再往它上面写表现样式，
 *      否则会重演 element-animation-paused 那种"钩子类兼表现"的双重职责
 * 2. 时间窗：data-schedule-from / data-schedule-to
 *    - 推荐写法：ISO8601 且带时区偏移，如 2026-09-17T15:00:00+08:00
 *    - 闭区间：from <= 当前时间 <= to 时显示，两端都含
 *    - 不做限制：不写该属性、写空值、写 0，三种写法等价
 *    - 容错：只写日期（2026-09-17）或漏写时区偏移（2026-09-17T15:00:00 ，
 *      含空格分隔写法）时，按北京时间（+08:00）解析并 warn。
 *      原因：new Date 对这两种写法会按 UTC（比北京早 8 小时）或访问者
 *      本地时区解析，不同地区生效时刻不一致，属于静默错位
 * 3. 初始态：元素必须自带 hidden 属性
 *    - JS 执行前先靠 hidden 隐藏，避免已过期内容闪现（FOUC）
 *    - 判定后由本模块增删 hidden；不写内联 display，
 *      元素原有的 display（flex/grid 等）不受影响
 * 4. 人工标签：data-schedule-id（可选，可为空）
 *    - 仅用于人工查找元素与日志定位，不参与任何判定逻辑
 *
 * ── 判定策略（配置出问题时的取舍） ─────────────────────────────────
 * - 只有标记、没有任何时间属性 → 视为无时间限制（显示）并 warn
 *   理由：内容该出现却没出现，比内容多出现更难被发现
 * - 时间格式无效、或 from 晚于 to → 视为不满足（隐藏）并 warn
 *   理由：保守处理，避免展示可能已过期或未生效的内容；同时"预期出现的内容
 *   没出现"在页面上是可见的，便于及时排查
 * - 不做定时切换：页面停留期间跨过起止时间点不会自动切换，需要刷新页面
 *
 * ── 已知边界 ───────────────────────────────────────────────────────
 * - 被隐藏的元素尺寸为 0，凡作用在它上面的组件（Swiper、IntersectionObserver、
 *   曝光埋点）都会拿到 0 尺寸或不触发；本模块在本 bundle 的 ready 回调中执行，
 *   早于页面级 JS，但若页面组件确实作用在会被隐藏的容器上，需自行留意顺序
 *
 * @module common/utils/schedule
 * @author wei<innereyecw@gmail.com>
 * @version 5.1.0
 */
(function (global) {
  "use strict";

  // 确保命名空间存在
  if (!global.watashi) {
    global.watashi = {};
  }
  if (!global.watashi.common) {
    global.watashi.common = {};
  }

  const common = global.watashi.common;
  const logger = (global.watashi.core && global.watashi.core.logger) || console;

  /** 容器标记类名（与 core/_states.scss 的样式契约对应，改动需同步） */
  const MARKER_CLASS = 'schedule-managed';

  /** 时间窗属性名 */
  const ATTR_FROM = 'data-schedule-from';
  const ATTR_TO = 'data-schedule-to';

  /** 人工标签属性名（可选、可为空） */
  const ATTR_ID = 'data-schedule-id';

  /** 视为"该方向不做限制"的取值：不写属性、空值、字面量 0 三者等价 */
  const UNLIMITED_VALUE = '0';

  /** 时区偏移：Z 或 ±HH:MM / ±HHMM */
  const TIMEZONE_OFFSET_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;

  /** 只写日期：YYYY-MM-DD */
  const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

  /** 写了日期和时间、但漏写时区偏移（含空格分隔写法） */
  const LOCAL_DATETIME_PATTERN = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)$/;

  /** 容错基准：漏写时区偏移时统一按北京时间理解 */
  const BEIJING_OFFSET = '+08:00';

  /**
   * 扫描选择器
   * 以容器标记为准；同时兜住"写了时间属性但漏写标记类"的元素，
   * 这类元素若不处理，其初始 hidden 会一直留着，导致内容永久消失
   */
  const SCAN_SELECTOR = '.' + MARKER_CLASS + ', [' + ATTR_FROM + '], [' + ATTR_TO + ']';

  /**
   * 是否已完成首次扫描
   * 首次扫描才检查"漏写 hidden"，重扫时 hidden 已被本模块改写，再查会误报
   */
  let initialized = false;

  /**
   * 生成元素的简短描述，用于日志里指认是哪一个容器
   * 优先用人工标签，其次退化为"标签名 + 前几个类名"
   *
   * @param {Element} element - 容器元素
   * @param {number}  [index] - 在本次扫描结果中的序号（从 0 开始）
   * @returns {string} 描述文本，如 data-schedule-id="cpb__gwp_banner_2607"
   */
  function describeElement(element, index) {
    const customId = element.getAttribute(ATTR_ID);
    if (customId) {
      return ATTR_ID + '="' + customId + '"';
    }

    const classNames = (typeof element.className === 'string' ? element.className : '')
      .trim()
      .split(/\s+/)
      .slice(0, 3)
      .join('.');
    const position = typeof index === 'number' ? '（第 ' + (index + 1) + ' 个）' : '';

    return '<' + element.tagName.toLowerCase() + (classNames ? '.' + classNames : '') + '>' + position;
  }

  /**
   * 把时间值规范成各浏览器都能稳定解析的形式
   * 只处理两件事：漏写时区偏移、只写日期 —— 统一按北京时间（+08:00）补齐，
   * 避免 new Date 按 UTC（比北京早 8 小时）或访问者本地时区解析造成静默错位
   *
   * @param {string} value - 属性值（已 trim）
   * @returns {string|null} 规范化后的时间字符串；无法识别时返回 null
   */
  function normalizeTimeValue(value) {
    // 已带时区偏移（Z 或 ±HH:MM）：按原值解析，不做干预
    if (TIMEZONE_OFFSET_PATTERN.test(value)) {
      return value;
    }

    // 只写日期：视为北京时间当天 00:00
    if (DATE_ONLY_PATTERN.test(value)) {
      return value + 'T00:00:00' + BEIJING_OFFSET;
    }

    // 有日期和时间、缺偏移：补上北京时间偏移
    // （顺带兼容 "2026-09-17 15:00:00" 这种空格分隔写法，Safari/iOS 原本不认）
    const matched = value.match(LOCAL_DATETIME_PATTERN);
    if (matched) {
      return matched[1] + 'T' + matched[2] + BEIJING_OFFSET;
    }

    return null;
  }

  /**
   * 读取并解析时间窗属性
   * 三种结果必须区分：不做限制（不写 / 空值 / 写 0）、已配置且有效、已配置但格式非法
   *
   * @param {Element} element - 容器元素
   * @param {string}  attr    - 属性名
   * @param {string}  desc    - 元素描述（仅用于日志）
   * @returns {{absent: boolean, configured: boolean, time: (Date|null), invalid: boolean}}
   *          absent=true 表示属性完全没写；configured=false 表示该方向不做限制；
   *          invalid=true 表示写了但格式无法识别
   */
  function readTimeAttribute(element, attr, desc) {
    const raw = element.getAttribute(attr);
    const value = raw === null ? '' : raw.trim();

    // 不做限制：没写属性、空值、字面量 0 三者等价
    if (value === '' || value === UNLIMITED_VALUE) {
      return {absent: raw === null, configured: false, time: null, invalid: false};
    }

    // 容错：漏写时区偏移或只写日期时按北京时间补齐（见 normalizeTimeValue）
    const normalized = normalizeTimeValue(value);
    const time = new Date(normalized === null ? value : normalized);

    // 格式非法：只负责报出原因，是否隐藏由调用方按策略决定
    if (isNaN(time.getTime())) {
      logger.warn('[Schedule] ' + desc + ' 的 ' + attr + ' 时间格式无法识别："' + value + '"。'
        + '可用形式：2026-09-17T15:00:00+08:00（推荐），'
        + '或 2026-09-17T15:00:00 / 2026-09-17（按北京时间理解）。');
      return {absent: false, configured: true, time: null, invalid: true};
    }

    if (normalized === null) {
      // 能解析但不是标准 ISO 形态（如 2026/09/17）：这类写法按访问者本地时区
      // 解析，不同地区生效时刻可能不同，提示作者改用规范写法
      logger.warn('[Schedule] ' + desc + ' 的 ' + attr + ' 不是标准 ISO8601 写法："' + value + '"。'
        + '浏览器会按访问者本地时区解析，建议改成 2026-09-17T15:00:00+08:00。');
    } else if (normalized !== value) {
      logger.warn('[Schedule] ' + desc + ' 的 ' + attr + ' 未写时区偏移，已按北京时间解析："'
        + value + '" → "' + normalized + '"。建议直接写成带 +08:00 的 ISO8601 形式。');
    }

    return {absent: false, configured: true, time: time, invalid: false};
  }

  /**
   * 判断时间点是否落在时间窗内（闭区间，两端都含）
   *
   * @param {Date|null} from  - 起始时间，null 表示该方向不限制
   * @param {Date|null} to    - 结束时间，null 表示该方向不限制
   * @param {Date}      [now] - 参照时间点，默认取当前时间
   * @returns {boolean} 是否应显示
   */
  function isWithin(from, to, now) {
    const current = now || new Date();

    if (from && current < from) {
      return false;
    }
    if (to && current > to) {
      return false;
    }

    return true;
  }

  /**
   * 扫描页面上的受排期容器，并按时间窗应用显隐
   * 幂等：重复执行结果一致，可在异步插入内容后再执行一次
   * 整轮扫描共用同一个时间点：避免逐个容器各自取时间，导致跨边界时
   * 同一次加载出现前后不一致的判定（例如交接瞬间两档都被判为窗内/窗外）
   *
   * @returns {void}
   */
  function applySchedule() {
    const containers = document.querySelectorAll(SCAN_SELECTOR);

    // 页面没有这类容器时直接返回：本模块随 main.min.js 全站加载，
    // 不能给不相干的页面刷日志
    if (containers.length === 0) {
      return;
    }

    const now = new Date();                 // 本轮判定的统一时间点
    const checkInitialState = !initialized; // 仅首次扫描检查初始态
    const usedIds = [];                     // 用于发现重复的人工标签
    let visibleCount = 0;
    let hiddenCount = 0;

    containers.forEach(function (element, index) {
      const desc = describeElement(element, index);
      const hasMarker = element.classList.contains(MARKER_CLASS);

      // 1. 漏写容器标记：本次仍按属性处理，避免 hidden 一直留着让内容消失
      if (!hasMarker) {
        logger.warn('[Schedule] ' + desc + ' 写了时间属性但缺少 ' + MARKER_CLASS
          + ' 类标记，初始态样式不会生效，请补上 class。');
      }

      // 2. 漏写初始态：JS 执行前会先显示一次
      if (checkInitialState && !element.hasAttribute('hidden')) {
        logger.warn('[Schedule] ' + desc + ' 缺少 hidden 属性，JS 执行前会先显示一次（闪烁），建议补上。');
      }

      // 3. 人工标签重复（复制模板常忘记修改）
      const customId = element.getAttribute(ATTR_ID);
      if (customId) {
        if (usedIds.indexOf(customId) !== -1) {
          logger.warn('[Schedule] ' + ATTR_ID + '="' + customId + '" 在页面内重复出现，'
            + '可能复制模板后未修改，会影响日志定位。');
        } else {
          usedIds.push(customId);
        }
      }

      // 4. 读取时间窗
      const from = readTimeAttribute(element, ATTR_FROM, desc);
      const to = readTimeAttribute(element, ATTR_TO, desc);
      let shouldShow;

      if (!from.configured && !to.configured) {
        // 两个属性都没写：大概率是漏写，提醒一句
        // （显式写空值或 0 是本模块支持的"不做限制"写法，视为有意为之，不再啰嗦）
        if (from.absent && to.absent) {
          logger.warn('[Schedule] ' + desc + ' 只有 ' + MARKER_CLASS + ' 标记，未写 '
            + ATTR_FROM + ' / ' + ATTR_TO + '，按「无时间限制」处理并保持显示。');
        }
        shouldShow = true;
      } else if (from.invalid || to.invalid) {
        // 时间格式有问题：保守隐藏，避免展示可能已过期或未生效的内容
        shouldShow = false;
      } else if (from.time && to.time && from.time.getTime() > to.time.getTime()) {
        logger.warn('[Schedule] ' + desc + ' 的 ' + ATTR_FROM + ' 晚于 ' + ATTR_TO
          + '，时间窗无效，元素将被隐藏。');
        shouldShow = false;
      } else {
        shouldShow = isWithin(from.time, to.time, now);
      }

      // 5. 应用显隐：hidden 属性是状态的唯一真相源
      //    不写内联 display，避免覆盖元素原有的 display 取值
      if (element.hidden !== !shouldShow) {
        element.hidden = !shouldShow;
      }

      if (shouldShow) {
        visibleCount += 1;
      } else {
        hiddenCount += 1;
      }
    });

    initialized = true;

    logger.log('[Schedule] 已处理 ' + containers.length + ' 个容器（显示 '
      + visibleCount + ' 个 / 隐藏 ' + hiddenCount + ' 个）');
  }

  /**
   * 排期显隐模块
   * @namespace common.schedule
   */
  common.schedule = {
    /**
     * 初始化：扫描页面并按时间窗应用显隐
     * 模块自身已在 document.ready 后自动调用，一般无需手动执行
     *
     * @returns {void}
     */
    init: applySchedule,

    /**
     * 重新扫描（init 的别名）
     * 用于异步插入内容后重新判定，例如列表/轮播渲染完成后多出的容器
     *
     * @returns {void}
     * @example
     * watashi.common.schedule.rescan();
     */
    rescan: applySchedule
  };

  // ==================== 自动初始化 ====================
  // 本文件随 main.min.js 全站加载，在这里完成自初始化，页面无需显式调用；
  // 页面上没有受排期容器时只做一次空扫描，无任何副作用。
  if (typeof document.ready === 'function') {
    document.ready(applySchedule);
  } else {
    // document.ready 尚未加载时降级重试（与 core/index.js 的处理方式保持一致）
    setTimeout(function () {
      if (typeof document.ready === 'function') {
        document.ready(applySchedule);
      } else {
        document.addEventListener('DOMContentLoaded', applySchedule);
      }
    }, 100);
  }

})(typeof window !== "undefined" ? window : this);
